// @core/stores/user/UserStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { userApi } from '@core/api';
import type {
  UpdateUserRequest,
  User,
} from '@core/types/user';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class UserStore {
  // List state
  items: User[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: User | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, User>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): User | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: User | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Delete user account
   */
  async DeleteUser(id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await userApi.DeleteUser(id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === id) {
          this.currentItem = null;
        }

        this.removeFromCache(id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Get user by ID
   */
  async GetUser(id: string): Promise<User> {
    // Check cache first
    const cached = this.cache.get(id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await userApi.GetUser(id);
      const item = response.user;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * List users with filters
   */
  async ListUsers(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await userApi.ListUsers(params);

      runInAction(() => {
        this.items = response.users?.users ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update user data
   */
  async UpdateUser(id: string, data: UpdateUserRequest): Promise<User> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await userApi.UpdateUser(id, data);
      const item = response.user;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }
}

export const userStore = new UserStore();
